import i18n from '@/utils/i18n';
import React, { memo, useCallback, useState } from 'react';
import {
  ActionSheet,
  Button,
  Checkbox,
  Icons,
  Popover,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type {
  FilterDropdownMenuProps,
  FilterDropdownOption,
} from '@/experiences/sales-portal/components/filter-dropdown-menu';

const MOBILE_CHECK_ICON_SIZE = 20;
// Matches the prototype's 64px action-sheet rows
const MOBILE_ROW_MIN_HEIGHT = 64;
const MOBILE_COMPACT_ROW_MIN_HEIGHT = 52;
const LONG_LIST_THRESHOLD = 8;

export const FilterDropdownMenuMobile = <TKey extends string>({
  isOpen,
  mode = 'multi',
  onApply,
  onOpenChange,
  onToggle,
  options,
  title,
  trigger,
}: FilterDropdownMenuProps<TKey>) => {
  const isLong = options.length > LONG_LIST_THRESHOLD;

  return (
    <>
      {trigger}
      <ActionSheet
        onOpenChange={onOpenChange}
        open={isOpen}
        size={isLong ? 'tall' : 'fit'}
      >
        {isOpen ? (
          <MobileFilterSheetContent
            isLong={isLong}
            mode={mode}
            onApply={onApply}
            onClose={() => onOpenChange(false)}
            onToggle={onToggle}
            options={options}
            title={title}
          />
        ) : null}
      </ActionSheet>
    </>
  );
};

interface MobileFilterSheetContentProps<TKey extends string> {
  isLong: boolean;
  mode: 'multi' | 'single';
  onApply?: (selectedKeys: TKey[]) => void;
  onClose: () => void;
  onToggle: (key: TKey) => void;
  options: FilterDropdownOption<TKey>[];
  title?: string;
}

const selectedKeysOf = <TKey extends string>(
  options: FilterDropdownOption<TKey>[],
) => new Set(options.filter((option) => option.selected).map((o) => o.key));

interface MobileFilterSheetRowProps<TKey extends string> {
  checked: boolean;
  compact: boolean;
  isMulti: boolean;
  onPress: (key: TKey) => void;
  option: FilterDropdownOption<TKey>;
}

const MobileFilterSheetRowComponent = <TKey extends string>({
  checked,
  compact,
  isMulti,
  onPress,
  option,
}: MobileFilterSheetRowProps<TKey>) => {
  const [styles, theme] = useStyles();

  return (
    <Pressable
      accessibilityLabel={option.label}
      accessibilityRole={isMulti ? 'checkbox' : 'radio'}
      aria-checked={checked}
      disabled={option.disabled}
      onPress={() => onPress(option.key)}
      style={[styles.mobileRow, compact ? styles.mobileRowCompact : null]}
    >
      <View style={styles.mobileRowText}>
        <Typography
          color={option.disabled ? '$textDisabled' : '$textPrimary'}
          fontWeight="$medium"
          size="$md"
        >
          {option.label}
        </Typography>
        {option.countLabel ? (
          <Typography color="$textSecondary" size="$sm">
            {option.countLabel}
          </Typography>
        ) : null}
      </View>
      {isMulti ? (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
        >
          <Checkbox checked={checked} disabled={option.disabled} size="$md" />
        </View>
      ) : checked ? (
        <Icons.Check
          color={theme.sk.color.icon.primary}
          size={MOBILE_CHECK_ICON_SIZE}
        />
      ) : null}
    </Pressable>
  );
};

const MobileFilterSheetRow = memo(
  MobileFilterSheetRowComponent,
) as typeof MobileFilterSheetRowComponent;

const MobileFilterSheetContent = <TKey extends string>({
  isLong,
  mode,
  onApply,
  onClose,
  onToggle,
  options,
  title,
}: MobileFilterSheetContentProps<TKey>) => {
  const [styles] = useStyles();
  const insets = useSafeAreaInsets();
  const [draft, setDraft] = useState(() => selectedKeysOf(options));
  const [pinnedKeys] = useState(() => selectedKeysOf(options));

  const isMulti = mode === 'multi';

  const orderedOptions = isLong
    ? [
        ...options.filter((option) => pinnedKeys.has(option.key)),
        ...options
          .filter((option) => !pinnedKeys.has(option.key))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ]
    : options;

  const toggleDraft = useCallback((key: TKey) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const apply = () => {
    const changedOptions = options.filter(
      (option) => option.selected !== draft.has(option.key),
    );
    if (changedOptions.length === 0) {
      onClose();
      return;
    }
    if (onApply) {
      onApply(
        options
          .filter((option) => draft.has(option.key))
          .map((option) => option.key),
      );
    } else {
      changedOptions.forEach((option) => onToggle(option.key));
    }
    onClose();
  };

  const applyLabel =
    isLong && draft.size > 0
      ? i18n.t('apply_with_count', { count: draft.size })
      : i18n.t('apply');

  const rows = orderedOptions.map((option) => (
    <MobileFilterSheetRow
      checked={isMulti ? draft.has(option.key) : option.selected}
      compact={isLong}
      isMulti={isMulti}
      key={option.key}
      onPress={isMulti ? toggleDraft : onToggle}
      option={option}
    />
  ));

  return (
    <>
      {title ? (
        <ActionSheet.Header marginBottom="$8" paddingBottom={0}>
          <ActionSheet.Title
            color="$textPrimary"
            fontSize="$xl"
            fontWeight="$medium"
          >
            {title}
          </ActionSheet.Title>
        </ActionSheet.Header>
      ) : null}
      {isLong ? (
        <Popover.Sheet.ScrollView style={styles.mobileScroll}>
          {rows}
        </Popover.Sheet.ScrollView>
      ) : (
        <ActionSheet.Body>{rows}</ActionSheet.Body>
      )}
      {isMulti ? (
        <XStack
          alignItems="center"
          justifyContent="space-between"
          paddingBottom={insets.bottom}
          paddingTop="$16"
        >
          <Button
            accessibilityLabel={i18n.t('clear_all')}
            disabled={draft.size === 0}
            onPress={() => setDraft(new Set())}
            size="$lg"
            variant="tertiary"
          >
            {i18n.t('clear_all')}
          </Button>
          <Button
            accessibilityLabel={applyLabel}
            onPress={apply}
            size="$lg"
            variant="primary"
            width="50%"
          >
            {applyLabel}
          </Button>
        </XStack>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  mobileRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    justifyContent: 'space-between',
    minHeight: MOBILE_ROW_MIN_HEIGHT,
  },
  mobileRowCompact: {
    minHeight: MOBILE_COMPACT_ROW_MIN_HEIGHT,
  },
  mobileRowText: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  mobileScroll: {
    flex: 1,
  },
}));
